import React, { useEffect, useMemo, useRef, useState } from "react";
import { get856Reconciliation, refresh856Reconciliation } from "../utils/ediApi";

const SUMMARY = [
  ["expected", "Expected"], ["accepted", "Accepted"], ["waiting", "Waiting"],
  ["blocked", "Blocked"], ["missing", "Missing"], ["rejectedOrAttention", "Rejected / Attention"],
];

const STATUS_LABELS = {
  ACCEPTED: "Accepted",
  GENERATED_UNSENT: "Generated, Unsent",
  SUBMITTING: "Submitting",
  AWAITING_MDN: "Waiting for AS2 Confirmation",
  AWAITING_997: "Waiting for Walmart 997",
  BLOCKED: "Blocked",
  MISSING_856: "Missing 856",
  REJECTED: "Rejected",
  IT_ATTENTION_REQUIRED: "IT Attention Required",
};

function localToday() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function shown(value) { return value === null || value === undefined || value === "" ? "—" : String(value); }
function reasonLabel(value) {
  return String(value || "").toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function Walmart856Reconciliation({ full, headers, onOpenBatch }) {
  const [shippedDate, setShippedDate] = useState(localToday());
  const [value, setValue] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshNotice, setRefreshNotice] = useState("");
  const requestId = useRef(0);

  useEffect(() => () => { requestId.current += 1; }, []);

  const rows = useMemo(() => {
    const source = Array.isArray(value?.rows) ? value.rows : [];
    return showAll ? source : source.filter((row) => row.classification !== "ACCEPTED");
  }, [showAll, value]);

  const run = async (event) => {
    event.preventDefault();
    if (!shippedDate) return;
    const id = ++requestId.current;
    const requestedDate = shippedDate;
    setLoading(true); setError(""); setRefreshNotice(""); setShowAll(false);
    try {
      if (full) {
        const refresh = await refresh856Reconciliation(requestedDate, { headers });
        if (id !== requestId.current) return;
        const created = Number(refresh?.createdReady || 0);
        const blocked = Number(refresh?.blocked || 0);
        if (created || blocked) {
          setRefreshNotice(`Shipment facts refreshed for ${created} PO${created === 1 ? "" : "s"}. ${blocked} PO${blocked === 1 ? "" : "s"} still require${blocked === 1 ? "s" : ""} attention.`);
        }
      }
      const next = await get856Reconciliation(requestedDate, { headers });
      if (id === requestId.current) setValue(next);
    } catch (exception) {
      if (id === requestId.current) {
        setValue(null); setError(exception?.message || "Unable to load Walmart 856 reconciliation.");
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  const changeDate = (nextDate) => {
    requestId.current += 1;
    setShippedDate(nextDate); setValue(null); setError(""); setRefreshNotice(""); setShowAll(false); setLoading(false);
  };

  return <section className="space-y-5">
    <form onSubmit={run} className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
      <label className="text-sm font-medium">Shipped Date
        <input aria-label="Reconciliation Shipped Date" type="date" required value={shippedDate}
          onChange={(event) => changeDate(event.target.value)} className="mt-1 block rounded border px-3 py-2"/>
      </label>
      <button type="submit" disabled={loading || !shippedDate} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">
        {loading ? "Reconciling..." : "Reconcile Date"}
      </button>
    </form>
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</div>}
    {refreshNotice && <div role="status" className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">{refreshNotice}</div>}
    {value && <>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {SUMMARY.map(([key, label]) => <div key={key} className="rounded-lg border bg-white p-3">
          <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{value.summary?.[key] ?? 0}</div>
        </div>)}
      </div>
      {(value.populationWarnings?.operationalInvalidShipDateCount || 0) > 0 && <div role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm font-medium text-amber-900">
        Action required: {value.populationWarnings.operationalInvalidShipDateCount} current shipped record(s) have an invalid or missing shipped date and cannot be included in date-specific 856 reconciliation.
      </div>}
      {(value.populationWarnings?.legacyInvalidShipDateCount || 0) > 0 && <div role="status" className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        {value.populationWarnings.legacyInvalidShipDateCount} legacy shipped record(s) use an unsupported historical date format and are excluded from date-specific reconciliation.
      </div>}
      {Array.isArray(value.loads) && value.loads.length > 0 && <section className="rounded-lg border bg-white p-4">
        <h2 className="font-semibold">Loads for {value.shippedDate}</h2>
        <div className="mt-3 flex flex-wrap gap-2">{value.loads.map((load) => <div key={load.loadId} className="flex items-center gap-3 rounded border px-3 py-2 text-sm">
          <span><strong>Load {shown(load.loadId)}</strong> · Expected {load.expected} · Accepted {load.accepted} · Action Needed {load.actionNeeded}</span>
          <button type="button" onClick={() => onOpenBatch(load.loadId)} className="rounded border bg-white px-2 py-1 text-blue-700">Open Batch</button>
        </div>)}</div>
      </section>}
      <section className="rounded-lg border bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-semibold">{showAll ? "All Expected 856s" : "856 Exceptions and Actions"}</h2>
            <p className="text-sm text-slate-500">Shipped date is the completeness boundary. Send actions remain in Batch 856.</p></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)}/>Show all Expected</label>
        </div>
        {!rows.length ? <p className="mt-4 rounded bg-emerald-50 p-3 text-sm text-emerald-900">No 856 exceptions require action for this shipped date.</p> :
          <div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm">
            <thead><tr className="border-b bg-slate-50"><th className="p-2">PO</th><th className="p-2">Load</th><th className="p-2">Ship Date</th><th className="p-2">Destination</th><th className="p-2">Status</th><th className="p-2">Reason</th><th className="p-2">Action</th></tr></thead>
            <tbody>{rows.map((row) => <tr key={`${row.custCode}|${row.loadId}|${row.poNumber}`} className="border-b">
              <td className="p-2 font-medium">{shown(row.poNumber)}</td><td className="p-2">{shown(row.loadId)}</td><td className="p-2">{shown(row.shipDate)}</td>
              <td className="p-2">{shown(row.destination?.name)}</td><td className="p-2">{STATUS_LABELS[row.classification] || shown(row.classification)}</td>
              <td className="p-2">{(row.reasons || []).length ? row.reasons.map(reasonLabel).join("; ") : "—"}</td>
              <td className="p-2">{row.loadId && row.safeNextAction !== "NONE" ? <button type="button" onClick={() => onOpenBatch(row.loadId)} className="rounded border px-2 py-1 text-blue-700">Open Batch</button> : "—"}</td>
            </tr>)}</tbody>
          </table></div>}
      </section>
    </>}
  </section>;
}
